import { InfrastructureProvider } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/InfrastructureProvider";
import { SubTypeInputs } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/subTypeInputs";
import { InfrastructureForm } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { FormFieldWithCustomInput } from "features/CenterContent/shared/operationalInformation/components/FormFieldWithCustomInput";
import { useOperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { filterOpInfoTypesInfrastructure } from "features/CenterContent/shared/operationalInformation/utils";
import { FC, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Select } from "shared/components/forms/Select";
import { Alert, Flex, Skeleton, Stack } from "shared/components/Spor";
import { getInfrastructureCustomInputs } from "shared/types/form";
import {
  isCommonInfraStructureEvent,
  isCustomInfraStructureEvent,
  isPlannedInfraStructureEvent,
} from "shared/utils/infrastructureEvent";
import { useDropsRole } from "stores/useDropsRole";

export const FormInputs: FC = () => {
  const { t } = useTranslation();
  const { data: opInfoTypes, status: opInfoTypesStatus } =
    useOperationalInformationTypes(t);
  const { register, setValue, watch, clearErrors } =
    useFormContext<InfrastructureForm>();

  const stretchName = watch("infrastructureForm.stretchName");
  const eventType = watch("infrastructureForm.type");

  const isPlanned = isPlannedInfraStructureEvent(eventType);
  const isCommon = isCommonInfraStructureEvent(eventType);
  const isCustom = isCustomInfraStructureEvent(eventType);

  const { isDriftledningen } = useDropsRole();

  const [lastEventWasCustom, setLastEventWasCustom] = useState(isCustom);

  useEffect(() => {
    if (isPlanned) {
      setValue("infrastructureForm.reason.type", "WORK_ON_TRACK");
    } else if (isCustom) {
      // Only nullable for custom events
      setValue("infrastructureForm.reason", null);
    } else if (lastEventWasCustom) {
      // Reset the type only when changing from `isCustom`,
      // in order to get rid of `NO_REASON`
      setValue("infrastructureForm.reason.type", null);
    }

    setLastEventWasCustom(isCustom);

    if (eventType) {
      clearErrors("infrastructureForm");
    }
  }, [eventType]);

  const infrastructureCustomInputs = getInfrastructureCustomInputs(t);

  switch (opInfoTypesStatus) {
    case "success": {
      const { events, reasons, consequences, actions } =
        filterOpInfoTypesInfrastructure(opInfoTypes, isPlanned);
      return (
        <Stack gap="18px">
          <Flex gap="6px" w="100%">
            <FormFieldWithCustomInput<InfrastructureForm>
              items={events}
              {...infrastructureCustomInputs.type}
              showWarningText
              hasTitleAndDescription
              titlePlaceholders={{
                NOB: stretchName,
                SWE: stretchName,
                ENG: stretchName,
              }}
            />
            {isCommon && !isDriftledningen() && (
              <Select
                label={t("Aksjonskort")}
                placeholder="-"
                {...register("infrastructureForm.actionCard.type")}
              >
                {opInfoTypes.actionCards.map((item) => (
                  <option key={item.type} value={item.type}>
                    {item.label}
                  </option>
                ))}
              </Select>
            )}
          </Flex>
          <InfrastructureProvider isPlanned={isPlanned} />
          {!isCustom && (
            <FormFieldWithCustomInput<InfrastructureForm>
              items={reasons}
              {...infrastructureCustomInputs.reason}
              disabled={isPlanned}
            />
          )}
          <FormFieldWithCustomInput<InfrastructureForm>
            items={consequences}
            {...infrastructureCustomInputs.consequence}
          />
          <FormFieldWithCustomInput<InfrastructureForm>
            items={actions}
            {...infrastructureCustomInputs.action}
          />
          <SubTypeInputs type={eventType} />
        </Stack>
      );
    }
    case "pending":
      return (
        <Stack gap="12px">
          <Skeleton height="36px" />
          <Skeleton height="36px" />
          <Skeleton height="36px" />
        </Stack>
      );
    case "error":
      return (
        <Alert data-color="critical">
          {t(
            "Noe gikk galt ved henting av hendelser, aksjonskort, årsaker og konsekvenser. Last siden på nytt for å prøve igjen. Ta kontakt med IT hvis feilen vedvarer.",
          )}
        </Alert>
      );
  }
};
